import { Card, Flex, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useGroveSchedulerApi } from '~/atoms/GroveSchedulerApi'
import { useUserState } from '~/atoms/UserState'

import { useListIntegrationAgents } from '@mangovoice/grove-scheduler-client'

import { IntegrationAgentCard } from './components/IntegrationAgentCard'
import { DefaultSettingsWrapper, SubheadingSection } from './Shared'

/**
 * Integrations Settings Page
 * Shows integration agents for the customer's company
 * Allows customers to authorize their agents
 */
export const IntegrationsSettings = () => {
  const { t } = useTranslation()
  const { pbxId, userRoles } = useUserState()
  const client = useGroveSchedulerApi()

  // Check if user is Portal Limited (restricted from integrations)
  const isPortalLimited = userRoles?.roles?.some(
    (role) => role.type === 'Portal Limited',
  )

  // Staff and superusers bypass portal restrictions
  const hasAccess =
    userRoles?.is_staff || userRoles?.is_superuser || !isPortalLimited

  // Query integration agents from API (must be called before conditional return)
  // Backend automatically filters by authenticated user's company
  const { data, isLoading, isError, error } = useListIntegrationAgents({
    client,
    options: {
      enabled: !!pbxId && hasAccess,
    },
  })

  const agents = data?.agents ?? []

  // Show access restricted message for Portal Limited users
  if (!hasAccess) {
    return (
      <DefaultSettingsWrapper>
        <SubheadingSection>
          <VStack gap={4} align="center" p={8}>
            <Text color="fg.muted" textAlign="center">
              {t('integrationsSettings.accessRestricted')}
            </Text>
          </VStack>
        </SubheadingSection>
      </DefaultSettingsWrapper>
    )
  }

  return (
    <DefaultSettingsWrapper>
      <SubheadingSection>
        <Heading size="lg" mb={4}>
          {t('integrationsSettings.title')}
        </Heading>
        <Text color="fg.muted" fontSize="sm" mb={6}>
          {t('integrationsSettings.description')}
        </Text>

        {isLoading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="xl" />
          </Flex>
        ) : isError ? (
          <Card.Root>
            <Card.Body p={6}>
              <VStack gap={2}>
                <Text color="fg.error" fontWeight="semibold">
                  {t('integrationsSettings.error.title')}
                </Text>
                <Text color="fg.muted" fontSize="sm">
                  {error?.message || t('integrationsSettings.error.message')}
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        ) : agents.length === 0 ? (
          <Card.Root>
            <Card.Body p={6}>
              <VStack gap={2}>
                <Text color="fg.muted">
                  {t('integrationsSettings.emptyState.primary')}
                </Text>
                <Text color="fg.muted" fontSize="sm">
                  {t('integrationsSettings.emptyState.secondary')}
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        ) : (
          <VStack gap={4} align="stretch">
            {agents.map((agent) => (
              <IntegrationAgentCard
                key={agent.agentId}
                partner={agent.partner}
                agentId={agent.agentId}
                config={
                  (agent.config as Record<string, string | boolean> | null) ||
                  {}
                }
                active={agent.active}
                createdAt={agent.createdAt}
                updatedAt={agent.updatedAt}
              />
            ))}
          </VStack>
        )}
      </SubheadingSection>
    </DefaultSettingsWrapper>
  )
}
